import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import {
  useConfirmPromoPostPostedMutation,
  useDeletePromoPostMutation,
  useMovePromoPostMutation,
  usePatchPromoPostMutation,
  useSchedulePromoPostMutation,
} from '../../../../redux/api/fymAPI';
import { ErrorAPIResult, isSuccessfulResult } from '../../../../redux/api/utils/isSuccessfulResult';
import { useShowToast } from '../../../../redux/slices/toasts/hooks';
import { isQuotaError, postErrorMessage } from './calendarPostErrors';

/** The answer of a write: done, or the error of the server. */
export type CalendarWriteResult = { ok: true } | { ok: false; error: unknown };

/**
 * The writes of the Calendar, each one with the same error rule: a 402 (no scheduled post left
 * this month) opens the upgrade prompt (the base query does it), each other refusal shows one
 * sentence. Each write
 * invalidates the Calendar read, and the realtime event tells the other tabs.
 */
export function useCalendarPostActions(accountId: number) {
  const intl = useIntl();
  const { showError } = useShowToast();
  const [movePost, moveState] = useMovePromoPostMutation();
  const [schedulePost, scheduleState] = useSchedulePromoPostMutation();
  const [patchPost, patchState] = usePatchPromoPostMutation();
  const [deletePost, deleteState] = useDeletePromoPostMutation();
  const [confirmPosted, confirmState] = useConfirmPromoPostPostedMutation();

  /** Says the error, unless `quiet` returns true for it (the caller says it in its own way). */
  const settle = useCallback(
    (result: { data: unknown } | ErrorAPIResult, quiet?: (error: unknown) => boolean): CalendarWriteResult => {
      if (isSuccessfulResult(result)) {
        return { ok: true };
      }
      const { error } = result;
      // The base query opens the upgrade prompt for a 402 by itself (`dispatchM24LimitPrompt`).
      if (isQuotaError(error)) {
        return { ok: false, error };
      }
      if (quiet === undefined || !quiet(error)) {
        showError(postErrorMessage(intl, error));
      }
      return { ok: false, error };
    },
    [intl, showError]
  );

  const move = useCallback(
    async (postUuid: string, scheduledAt: Date, quiet?: (error: unknown) => boolean) =>
      settle(await movePost({ accountId, postUuid, scheduledAt: scheduledAt.toISOString() }), quiet),
    [accountId, movePost, settle]
  );

  const schedule = useCallback(
    async (postUuid: string) => settle(await schedulePost({ accountId, postUuid })),
    [accountId, schedulePost, settle]
  );

  /** A new time for a failed post: the server arms it again and takes no new unit. */
  const reschedule = useCallback(
    async (postUuid: string, scheduledAt: string) => settle(await patchPost({ accountId, postUuid, scheduledAt })),
    [accountId, patchPost, settle]
  );

  /** Cancels a scheduled post. The unit goes back to this month. */
  const cancel = useCallback(
    async (postUuid: string) => settle(await patchPost({ accountId, postUuid, status: 'canceled' })),
    [accountId, patchPost, settle]
  );

  const remove = useCallback(
    async (postUuid: string) => settle(await deletePost({ accountId, postUuid })),
    [accountId, deletePost, settle]
  );

  const markPosted = useCallback(
    async (postUuid: string) => settle(await confirmPosted({ accountId, postUuid })),
    [accountId, confirmPosted, settle]
  );

  const isWriting =
    moveState.isLoading ||
    scheduleState.isLoading ||
    patchState.isLoading ||
    deleteState.isLoading ||
    confirmState.isLoading;

  // One object while nothing changes, so the memoised cards do not draw again on every render.
  return useMemo(
    () => ({ move, schedule, reschedule, cancel, remove, markPosted, isWriting }),
    [move, schedule, reschedule, cancel, remove, markPosted, isWriting]
  );
}

export type CalendarPostActions = ReturnType<typeof useCalendarPostActions>;
